import {
  AUDIT_JOB_CREATE_API_PATH,
  AUDIT_REQUEST_TIMEOUT,
  AUDIT_STATUS
} from './constants';
import { fetchJson } from './fetch';

const AUDIT_JOB_STATUSES = new Set([
  AUDIT_STATUS.QUEUED,
  AUDIT_STATUS.RUNNING,
  AUDIT_STATUS.PASSED,
  AUDIT_STATUS.REJECTED,
  AUDIT_STATUS.FAILED,
  AUDIT_STATUS.TIMEOUT,
  'pending'
]);

function normalizeDebugScenario(scenario) {
  if (!scenario) {
    return '';
  }

  const normalized = String(scenario).trim().toLowerCase();
  const aliases = {
    success: 'poll_running_then_passed',
    passed: 'poll_passed',
    rejected: 'poll_rejected',
    failed: 'poll_failed',
    timeout: 'poll_timeout',
    create_passed: 'poll_passed',
    create_rejected: 'poll_rejected',
    create_failed: 'poll_failed',
    create_timeout: 'poll_timeout',
    create_503: 'create_dispatch_failed',
    poll_404: 'poll_job_not_found'
  };

  return aliases[normalized] || normalized;
}

function normalizeJobStatus(status) {
  const normalizedStatus = String(status || '').trim().toLowerCase();
  if (AUDIT_JOB_STATUSES.has(normalizedStatus)) {
    return normalizedStatus;
  }
  return AUDIT_STATUS.QUEUED;
}

function normalizeJobResponse(data, fallback = {}) {
  const payload = data && typeof data === 'object' ? data : {};
  const totalCount = typeof payload.totalCount === 'number'
    ? payload.totalCount
    : (typeof fallback.totalCount === 'number' ? fallback.totalCount : 0);
  const now = Date.now();

  return {
    jobId: payload.jobId || fallback.jobId || '',
    albumKey: payload.albumKey || fallback.albumKey || '',
    status: normalizeJobStatus(payload.status || fallback.status),
    totalCount,
    auditedCount: typeof payload.auditedCount === 'number' ? payload.auditedCount : 0,
    passCount: typeof payload.passCount === 'number' ? payload.passCount : 0,
    pollAfterMs: typeof payload.pollAfterMs === 'number' ? payload.pollAfterMs : 600,
    rejectedImageUrl: payload.rejectedImageUrl || '',
    rejectedImageIndex: typeof payload.rejectedImageIndex === 'number' ? payload.rejectedImageIndex : -1,
    rejectedRiskLevel: payload.rejectedRiskLevel || '',
    rejectedLabel: payload.rejectedLabel || '',
    errorCode: payload.errorCode || '',
    errorMessage: payload.errorMessage || '',
    createdAt: typeof payload.createdAt === 'number' ? payload.createdAt : now,
    updatedAt: typeof payload.updatedAt === 'number' ? payload.updatedAt : now
  };
}

function createMockError(error, message) {
  const err = new Error(message || error || 'mock_error');
  err.code = error || 'MOCK_ERROR';
  err.payload = {
    error: error || 'MOCK_ERROR',
    message: message || error || 'mock_error'
  };
  return err;
}

function createMockJobPayload({ jobId, albumKey, totalCount, status, overrides = {} }) {
  return normalizeJobResponse(
    Object.assign({
      jobId,
      albumKey,
      status,
      totalCount
    }, overrides || {}),
    {
      jobId,
      albumKey,
      status,
      totalCount
    }
  );
}

function createRealAuditClient({ auditBaseUrl, albumKey, requestControllers }) {
  return {
    async createJob() {
      const response = await fetchJson(
        `${auditBaseUrl}${AUDIT_JOB_CREATE_API_PATH}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            albumKey,
            __app__: 'vliti',
            services: ['baselineCheck'],
            clientMeta: {
              scene: 'mainloading',
              source: 'td-share'
            }
          })
        },
        AUDIT_REQUEST_TIMEOUT,
        requestControllers,
        { type: 'create_job' }
      );

      return normalizeJobResponse(response, {
        albumKey,
        status: AUDIT_STATUS.QUEUED
      });
    },

    async getJob(jobId) {
      const response = await fetchJson(
        `${auditBaseUrl}${AUDIT_JOB_CREATE_API_PATH}/${encodeURIComponent(jobId)}`,
        { method: 'GET' },
        AUDIT_REQUEST_TIMEOUT,
        requestControllers,
        { type: 'poll_job', jobId }
      );

      return normalizeJobResponse(response, {
        jobId,
        albumKey,
        status: AUDIT_STATUS.QUEUED
      });
    }
  };
}

function createMockAuditClient({ debugScenario, albumKey, auditTotalCount }) {
  const scenario = normalizeDebugScenario(debugScenario);
  const jobId = 'audit_job_mock_mainloading';
  let pollRound = 0;

  return {
    async createJob() {
      switch (scenario) {
        case 'request_error_create':
          throw new Error('mock request error on create');
        case 'timeout_create': {
          const timeoutError = new Error('timeout');
          timeoutError.code = 'timeout';
          throw timeoutError;
        }
        case 'create_invalid_params':
          throw createMockError('INVALID_PARAMS', 'albumKey is required when images not provided');
        case 'create_no_images':
          throw createMockError('NO_IMAGES', 'no images found');
        case 'create_image_fetch_error':
          throw createMockError('IMAGE_FETCH_ERROR', 'failed to fetch images');
        case 'create_dispatch_failed':
          throw createMockError('DISPATCH_FAILED', 'dispatch failed');
        case 'create_job_store_unavailable':
          throw createMockError('JOB_STORE_UNAVAILABLE', 'job store unavailable');
        default:
          return createMockJobPayload({
            jobId,
            albumKey,
            totalCount: auditTotalCount,
            status: AUDIT_STATUS.QUEUED
          });
      }
    },

    async getJob(currentJobId) {
      switch (scenario) {
        case 'request_error_poll':
          throw new Error('mock request error on poll');
        case 'timeout_poll': {
          const timeoutError = new Error('timeout');
          timeoutError.code = 'timeout';
          throw timeoutError;
        }
        case 'poll_job_not_found':
          throw createMockError('JOB_NOT_FOUND', 'job not found');
        case 'poll_job_store_unavailable':
          throw createMockError('JOB_STORE_UNAVAILABLE', 'job store unavailable');
        case 'poll_passed':
          return createMockJobPayload({
            jobId: currentJobId,
            albumKey,
            totalCount: auditTotalCount,
            status: AUDIT_STATUS.PASSED,
            overrides: {
              auditedCount: auditTotalCount,
              passCount: auditTotalCount
            }
          });
        case 'poll_rejected':
          return createMockJobPayload({
            jobId: currentJobId,
            albumKey,
            totalCount: auditTotalCount,
            status: AUDIT_STATUS.REJECTED,
            overrides: {
              auditedCount: Math.min(8, auditTotalCount || 1),
              passCount: Math.max(0, Math.min(7, (auditTotalCount || 1) - 1)),
              rejectedImageUrl: 'https://example.com/rejected.jpg',
              rejectedImageIndex: Math.max(0, Math.min(7, (auditTotalCount || 1) - 1)),
              rejectedRiskLevel: 'high',
              rejectedLabel: 'porn'
            }
          });
        case 'poll_failed':
          return createMockJobPayload({
            jobId: currentJobId,
            albumKey,
            totalCount: auditTotalCount,
            status: AUDIT_STATUS.FAILED,
            overrides: {
              auditedCount: 3,
              passCount: 3,
              errorCode: 'AUDIT_TIMEOUT',
              errorMessage: 'image_download_failed: 403'
            }
          });
        case 'poll_timeout':
          return createMockJobPayload({
            jobId: currentJobId,
            albumKey,
            totalCount: auditTotalCount,
            status: AUDIT_STATUS.TIMEOUT,
            overrides: {
              auditedCount: 3,
              passCount: 3,
              errorCode: 'JOB_TIMEOUT',
              errorMessage: 'job total timeout after 300000ms'
            }
          });
        case 'poll_running_then_passed':
          if (pollRound === 0) {
            pollRound++;
            return createMockJobPayload({
              jobId: currentJobId,
              albumKey,
              totalCount: auditTotalCount,
              status: AUDIT_STATUS.RUNNING,
              overrides: {
                auditedCount: Math.min(7, Math.max(1, auditTotalCount - 1)),
                passCount: Math.min(7, Math.max(1, auditTotalCount - 1))
              }
            });
          }
          return createMockJobPayload({
            jobId: currentJobId,
            albumKey,
            totalCount: auditTotalCount,
            status: AUDIT_STATUS.PASSED,
            overrides: {
              auditedCount: auditTotalCount,
              passCount: auditTotalCount
            }
          });
        default:
          return createMockJobPayload({
            jobId: currentJobId,
            albumKey,
            totalCount: auditTotalCount,
            status: AUDIT_STATUS.QUEUED
          });
      }
    }
  };
}

function createDebugAuditClient({
  auditBaseUrl,
  albumKey,
  requestControllers,
  debugScenario,
  auditTotalCount
}) {
  const realClient = createRealAuditClient({
    auditBaseUrl,
    albumKey,
    requestControllers
  });
  const scenarioClient = createMockAuditClient({
    debugScenario,
    albumKey,
    auditTotalCount
  });

  return {
    async createJob() {
      const realResponse = await realClient.createJob();
      const debugResponse = await scenarioClient.createJob();

      if (debugResponse && typeof debugResponse === 'object') {
        return normalizeJobResponse(
          Object.assign({}, debugResponse, {
            jobId: realResponse && realResponse.jobId ? realResponse.jobId : debugResponse.jobId,
            albumKey: realResponse && realResponse.albumKey ? realResponse.albumKey : debugResponse.albumKey
          }),
          {
            albumKey,
            status: AUDIT_STATUS.QUEUED,
            totalCount: auditTotalCount
          }
        );
      }

      return normalizeJobResponse(realResponse, {
        albumKey,
        status: AUDIT_STATUS.QUEUED,
        totalCount: auditTotalCount
      });
    },

    async getJob(jobId) {
      await realClient.getJob(jobId);

      return scenarioClient.getJob(jobId);
    }
  };
}

export function createAuditClient({
  auditBaseUrl,
  albumKey,
  requestControllers,
  debugScenario,
  auditTotalCount
}) {
  if (normalizeDebugScenario(debugScenario)) {
    return createDebugAuditClient({
      auditBaseUrl,
      debugScenario,
      albumKey,
      requestControllers,
      auditTotalCount
    });
  }

  return createRealAuditClient({
    auditBaseUrl,
    albumKey,
    requestControllers
  });
}
